import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  inject,
  ViewChild,
  OnInit,
} from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { EnhancedLoadingService } from '../../../shared/services/enhanced-loading.service';
import { SmartOrpcClientService } from '../../../shared/services/smart-orpc-client.service';
import { GeographicDataService } from '../../../shared/services/geographic-data.service';
import {
  DatasafraSearchReportsComponent,
  ReportFilters,
  FilterFieldConfig,
} from '../../../shared/components/datasafra-search-reports/datasafra-search-reports.component';
import {
  ReportsTableComponent,
  ReportTableItem,
  ReportTableAction,
  ReportTableConfig,
  ReportTableColumn,
} from '../../../shared/components/reports-table/reports-table.component';
import { Sort } from '@angular/material/sort';
import { PageEvent } from '@angular/material/paginator';
import debounce from 'lodash/debounce';
import { UserService } from '../../../shared/services/user.service';
import {
  HistoriesSafrasReportItem,
  HistoriesSafrasReportFilters,
  HistoriesSafras,
  transformHistoriesSafrasToReportItem,
} from '../../../shared/types/histories-safras-report.types';
import { isAbortOrAbortCause } from '../../../shared/utils/is-abort-or-abort-cause';
import { QgisService } from '../../../qgis.service';
import { ActivatedRoute } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { createNestedOrder } from '../../../shared/utils/create-nested-order';
import { FeatureFlagsService } from '../../../shared/services/feature-flags.service';

@Component({
  selector: 'app-histories-safras-reports',
  standalone: true,
  imports: [
    CommonModule,
    DatasafraSearchReportsComponent,
    ReportsTableComponent,
  ],
  templateUrl: './histories-safras-reports.component.html',
  styleUrl: './histories-safras-reports.component.scss',
})
export class HistoriesSafrasReportsComponent implements AfterViewInit, OnInit {
  loadingService = inject(EnhancedLoadingService);
  smartClient = inject(SmartOrpcClientService);
  userService = inject(UserService);
  geographicDataService = inject(GeographicDataService);
  qgisService = inject(QgisService);
  private route = inject(ActivatedRoute);
  private snackBar = inject(MatSnackBar);
  private featureFlags = inject(FeatureFlagsService);

  dataSource = new MatTableDataSource<ReportTableItem>([]);
  originalData: ReportTableItem[] = [];
  length = 0;

  // Current filters and pagination state
  currentFilters: HistoriesSafrasReportFilters = {};
  currentSort: Sort = {
    active: 'batchHistoryRequest.createdDate',
    direction: 'desc',
  };

  currentPageIndex = 0;
  currentPageSize = 5;

  // Abort controller for cancelling requests
  abortController?: AbortController;

  // Observables para loading
  globalLoading$ = this.loadingService.global$;
  gridLoading$ = this.loadingService.grid$;
  inputLoading$ = this.loadingService.input$;

  // Configuration for dynamic filters - filtrar CAR baseado em feature flag
  filterConfig: FilterFieldConfig<HistoriesSafrasReportFilters>[] = [
    {
      key: 'organizationId',
      label: 'Cliente',
      type: 'autocomplete-org',
      visible: true,
      adminOnly: true,
    },
    {
      key: 'requester',
      label: 'Solicitante',
      type: 'text',
      visible: true,
      adminOnly: false,
    },
    {
      key: 'areaIdentification',
      label: 'Identificação da Área',
      type: 'text',
      visible: true,
    },
    {
      key: 'areaHaMin',
      label: 'Área Mínima (ha)',
      type: 'number',
      visible: true,
    },
    {
      key: 'areaHaMax',
      label: 'Área Máxima (ha)',
      type: 'number',
      visible: true,
    },
    {
      key: 'state',
      label: 'UF',
      type: 'select',
      visible: true,
    },
    {
      key: 'city',
      label: 'Município',
      type: 'select',
      visible: true,
    },
    { key: 'producer', label: 'Produtor', type: 'text', visible: true },
    { key: 'cpfCnpj', label: 'CPF/CNPJ', type: 'text', visible: true },
    {
      key: 'contract',
      label: 'Contrato',
      type: 'text',
    },
    {
      key: 'car',
      label: 'CAR',
      type: 'text',
    },
    {
      key: 'insurancePolicy',
      label: 'Apólice',
      type: 'text',
    },
    {
      key: 'farmName',
      label: 'Nome da Fazenda',
      type: 'text',
    },
    {
      key: 'requestDate',
      label: 'Data da Solicitação',
      type: 'date-range',
      visible: true,
    },
  ];

  tableColumns: ReportTableColumn[] = [
    {
      key: 'requestNumber',
      label: '# da Solicitação',
      sortable: true,
      visible: true,
      sortKey: 'batchHistoryRequest.id', // Campo real no banco
    },
    {
      key: 'client',
      label: 'Cliente',
      sortable: true,
      visible: true,
      adminOnly: true,
      sortKey: 'batchHistoryRequest.createdBy', // Campo real no banco
    },
    {
      key: 'requester',
      label: 'Solicitante',
      sortable: true,
      visible: true,
      adminOnly: false,
      sortKey: 'batchHistoryRequest.createdBy', // Campo real no banco
    },
    {
      key: 'requestDate',
      label: 'Data da Solicitação',
      sortable: true,
      visible: true,
      type: 'date',
      sortKey: 'batchHistoryRequest.createdDate', // Campo real no banco
    },
    {
      key: 'areaIdentification',
      label: 'Identificação da Área',
      sortable: true,
      visible: true,
      sortKey: 'areaId', // Campo real no banco
    },
    {
      key: 'area',
      label: 'Área (ha)',
      sortable: true,
      visible: true,
      sortKey: 'areaHa', // Campo real no banco
    },
    {
      key: 'city',
      label: 'Município',
      sortable: true,
      visible: true,
      sortKey: 'ibgeCity.nmMun', // Campo real no banco
    },
    {
      key: 'state',
      label: 'UF',
      sortable: true,
      visible: true,
      sortKey: 'ibgeCity.nmUf', // Campo real no banco
    },
    {
      key: 'cpfCnpj',
      label: 'CPF/CNPJ',
      sortable: true,
      visible: true,
      sortKey: 'cpfCnpj', // Campo real no banco
    },
    {
      key: 'producer',
      label: 'Produtor',
      sortable: true,
      visible: true,
      sortKey: 'producer', // Campo real no banco
    },
    {
      key: 'contract',
      label: 'Contrato',
      sortable: true,
      visible: true,
      sortKey: 'contract',
    },
    {
      key: 'car',
      label: 'CAR',
      sortable: true,
      visible: true,
      sortKey: 'car',
    },
    {
      key: 'insurancePolicy',
      label: 'Apólice',
      sortable: true,
      visible: true,
      sortKey: 'insurancePolicy',
    },
    {
      key: 'farmName',
      label: 'Nome da Fazenda',
      sortable: true,
      visible: true,
      sortKey: 'farmName',
    },
  ];

  // Configuração da tabela
  tableConfig: ReportTableConfig = {
    showActionsColumn: false,
    columns: this.tableColumns,
    initialSort: {
      active: 'batchHistoryRequest.createdDate',
      direction: 'desc',
    }, // Usar key do frontend, será mapeado automaticamente
    statusClassFunction: (statusCode: string) =>
      this.getStatusClass(statusCode),

    isAdmin: this.userService.isGeoambiente,
  };

  // Ações da tabela
  tableActions: ReportTableAction[] = [
    {
      key: 'download',
      label: 'Baixar',
      icon: 'file_download',
      action: (item: ReportTableItem) => {
        this.qgisService.downloadHarvestHistoryReport(
          item.id,
          item.areaIdentification,
        );
      },
    },
    {
      key: 'view',
      label: 'Visualizar',
      icon: 'visibility',
      action: (item: ReportTableItem) => this.viewReport(item),
    },
  ];

  @ViewChild(DatasafraSearchReportsComponent)
  declare searchComponent: DatasafraSearchReportsComponent;
  @ViewChild(ReportsTableComponent)
  declare tableComponent: ReportsTableComponent;

  // Debounced functions for different loading scopes
  debouncedFilterLoad = debounce(
    (filters: ReportFilters) => this.loadWithInputScope(filters),
    500,
  );

  debouncedGridLoad = debounce(
    (params: Partial<HistoriesSafrasReportFilters>) =>
      this.loadWithGridScope(params),
    300,
    { leading: true },
  );
  historiesRequestId: number | null = null;

  async ngOnInit() {
    const idRouteParam =
      this.route.snapshot.queryParamMap.get('batchRequestId');

    if (idRouteParam) {
      this.historiesRequestId = Number(idRouteParam);
    }

    this.filterConfig.unshift({
      key: 'batchRequestId',
      label: 'Número da Solicitação',
      type: 'number',
      defaultValue: this.historiesRequestId?.toString() ?? '',
    });
  }

  async ngAfterViewInit() {
    await this.loadInitialData();
  }

  alert = (message: string, duration = 5000) =>
    this.snackBar.open(message, 'Fechar', { duration });

  async downloadPage() {
    const items = this.dataSource.data;
    const len = items.length;
    if (len === 0) {
      this.alert('Nenhum relatório disponível para download nesta página.');
      return;
    }
    const alert = this.alert(
      `Baixando ${len} arquivo${len > 1 ? 's' : ''}...`,
      -1,
    );

    for await (const item of items) {
      await this.qgisService.downloadHarvestHistoryReport(
        item.id!,
        item.areaIdentification,
      );
    }
    alert.dismiss();
  }

  handleFilters(filters: HistoriesSafrasReportFilters) {
    // Handle state change to filter cities
    if (filters.state !== this.currentFilters.state) {
      this.geographicDataService.filterCitiesByState(filters.state || '');

      // Reset city filter when state changes
      if (filters.state !== this.currentFilters.state) {
        filters.city = undefined;
      }
    }

    this.currentFilters = { ...filters };
    this.tableComponent.paginator.firstPage(); // Reset to first page when filtering
    this.debouncedFilterLoad(filters);
  }

  onSortChange(event: Sort) {
    // O mapeamento já será feito pelo componente da tabela
    this.currentSort = event;
    this.debouncedGridLoad(this.currentFilters);
  }

  /**
   * Map frontend sort to backend sort using column configuration
   */
  private mapSortToBackend(sort: Sort): Sort {
    const column = this.tableColumns.find((col) => col.key === sort.active);

    if (column?.sortKey) {
      return {
        active: column.sortKey,
        direction: sort.direction,
      };
    }

    return sort;
  }

  onPageChange(event: PageEvent) {
    this.currentPageIndex = event.pageIndex;
    this.currentPageSize = event.pageSize;
    this.debouncedGridLoad(this.currentFilters); // Passa os filtros atuais
  }

  /**
   * Load data with input scope - for filters with immediate feedback
   */
  private async loadWithInputScope(filters: ReportFilters) {
    await this.loadData(
      { ...this.currentFilters, ...filters },
      'Erro na busca:',
    );
  }

  /**
   * Load data with grid scope - for pagination and sorting
   */
  private async loadWithGridScope(
    params: Partial<HistoriesSafrasReportFilters>,
  ) {
    await this.loadData(
      { ...this.currentFilters, ...params },
      'Erro no carregamento:',
    );
  }

  /**
   * Load initial data with global scope
   */
  private async loadInitialData() {
    this.searchComponent.applyFilters();
  }

  /**
   * Core data loading method
   */
  private async loadData(
    filters: HistoriesSafrasReportFilters,
    errorMsg: string,
  ) {
    // Cancel previous request
    this.abortController?.abort();
    this.abortController = new AbortController();

    const requestFilters: HistoriesSafrasReportFilters =
      this.getFullBackendInputFilters(filters);
    console.log('Loading data with filters:', requestFilters);
    const [data, total] =
      await this.smartClient.client.historiesSafras.reports.list(
        requestFilters,
        { signal: this.abortController.signal },
      );

    // Transform raw database entities to frontend report items
    const transformedData: HistoriesSafrasReportItem[] = (
      data as HistoriesSafras[]
    ).map(transformHistoriesSafrasToReportItem);
    console.log('Transformed data:', transformedData);

    this.dataSource.data = transformedData;
    this.length = total;
  }

  private getFullBackendInputFilters(
    filters: HistoriesSafrasReportFilters,
    isPaged = true,
  ): HistoriesSafrasReportFilters {
    // Map sort to backend column names
    const mappedSort = this.currentSort;
    return {
      batchRequestId: this.historiesRequestId ?? undefined,
      ...filters,
      // ALWAYS apply COMPLETO status filter - only show completed histories
      statusRequest: 'COMPLETED',
      cursor: isPaged
        ? (this.tableComponent.paginator?.pageIndex ?? 0) *
          (this.tableComponent.paginator?.pageSize ?? 0)
        : 0,
      limit: isPaged ? (this.tableComponent.paginator?.pageSize ?? 0) : 500,
      orderBy: mappedSort?.active ? createNestedOrder(mappedSort) : undefined,
    };
  }

  viewReport(item: ReportTableItem): void {
    console.log('Viewing report:', item);
    window
      .open(
        this.qgisService.getReportAtlasPrintUrl('harvestHistory', item.id),
        '_blank',
      )
      ?.focus();
  }

  getStatusClass(statusCode: string) {
    switch (statusCode) {
      case 'compliant':
        return 'chip-compliant';
      case 'non_compliant':
        return 'chip-non-compliant';
      default:
        return null;
    }
  }
}
